import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNotifications, type ApiOptions } from '../helpers';
import { CreateTemplateDocument } from './create.generated';

/**
 * Marks a GraphQL error whose message describes a problem with the uploaded zip
 * and is safe to show the user verbatim. Raised by
 * Mutations::Mixins::TemplateZipErrors.
 */
export const INVALID_TEMPLATE_ZIP = 'INVALID_TEMPLATE_ZIP';

export const useCreateTemplate = (options: ApiOptions<typeof CreateTemplateDocument> = {}) => {
  const { t } = useTranslation();

  return useMutation(CreateTemplateDocument, {
    ...useNotifications(CreateTemplateDocument, {
      // Only show the server's message for errors it marked as user-facing;
      // anything else could be an internal message.
      failure: (error) =>
        error.graphQLErrors[0]?.extensions?.code === INVALID_TEMPLATE_ZIP
          ? error.graphQLErrors[0].message
          : t('api.common.error_generic'),
      success: (data) =>
        data.createTemplate?.template?.name != null
          ? t('api.templates.create_success', { name: data.createTemplate.template.name })
          : t('api.templates.create_success_no_name'),
    }),

    // This was already there...I guess it's to invalidate the top-level query
    update(cache, { data }) {
      if (data?.createTemplate?.template == null) return;
      cache.evict({ fieldName: 'templates' });
    },

    ...options,
  });
};
